<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte';
  import { get } from 'svelte/store';
  import { currentView, stages, pollVersion, pollingActive, getScoreColors, georgeUnread, agentName, userTier, userDaysRemaining, subscriptionEndsAt, isAdmin, georgeDiscussContext, appTheme, stageColor, stageLabel, terms, STAGE_ORDER, userEmail, refreshStages, setClientStage } from '../lib/stores';
  import { getDashboard, getStageSources, setStage, resumeAutomaticStage, getIntentSignals, setFollowup, getDashboardTracking, createPortalSession, resumeSubscription, renameClient, pinClient, unpinClient, getDashboardViewStates, saveDashboardViewState, deleteDashboardViewState, type DashboardRow, type IntentSignal } from '../lib/api';
  import { readCache, writeCache } from '../lib/swr';
  import { followupStatus } from '../lib/followup';

  // Rows are a fast first paint, scoped to the signed-in user. Stages are
  // fetched from the server; a browser snapshot must not overwrite fresh stages.
  const DASH_ROWS_CACHE = (mine: boolean) => `dashboard_rows_v2_${$userEmail}_${mine ? 'mine' : 'all'}`;
  const LAST_MINE_KEY = `dashboard_last_mine_v2_${$userEmail}`;
  // Read the last-shown variant's cache ONCE, synchronously at init, so the
  // very FIRST render already has rows — no empty-frame flash when navigating
  // back to the page (the component is recreated on nav, resetting state).
  // onMount still revalidates and refines the variant.
  const _lastMine = readCache<boolean>(LAST_MINE_KEY) ?? false;
  const _dashInit = readCache<{ built_at: string; rows: DashboardRow[] }>(DASH_ROWS_CACHE(_lastMine));
  // Persisted stage-group expansion state — init synchronously so the first
  // render shows the SAME expanded/collapsed groups, with no compact→expand
  // pop while the user watches.
  const EXPANDED_CACHE = `dashboard_expanded_v2_${$userEmail}`;
  const _expInit = readCache<string[]>(EXPANDED_CACHE);
  // Which variant the current `rows`/`builtAt` belong to — guards the
  // version-gate so a variant switch with no cache can't wrongly skip.
  let loadedVariant = $state<boolean | null>(_dashInit ? _lastMine : null);
  function hydrateDash(mine: boolean) {
    const c = readCache<{ built_at: string; rows: DashboardRow[] }>(DASH_ROWS_CACHE(mine));
    if (c) { rows = c.rows || []; builtAt = c.built_at || ''; loadedVariant = mine; }
    else { rows = []; builtAt = ''; loadedVariant = null; }
  }
  import { pinnedClients, colorScheme, scoreColorFn, scoreBadgeStyle as sharedScoreBadgeStyle } from '../lib/stores';
  import { renderSafeMarkdown } from '../lib/markdown';
  import UpgradeModal from '../lib/UpgradeModal.svelte';
  import BugReportModal from '../lib/BugReportModal.svelte';
  import SyncButton from '../lib/SyncButton.svelte';

  let showBugModal = $state(false);

  // ── Filter + saved-view model (matches designs/dashboard/dashboard-stage-grouped-full.html) ──
  // Three independent filter axes, each a multi-select; empty = "All".
  //   • stage  — client stage ($stages[client])
  //   • status — follow-up bucket (On track / Soon / Overdue / Parked)
  //   • owner  — currently only "Me" (backend my_deals refetch). Per-owner
  //              filtering needs per-row owner data (archive/todos/dashboard-feature.md).
  // A "view" is a named filter+sort PRESET. Builtins are fixed; custom views are
  // user-saved and persist through the generic /dashboard/view-state KV store.
  // sortDir: 'asc' = oldest/lowest first; 'desc' = newest/highest first.
  type SortKey = 'client' | 'stage' | 'status' | 'score' | 'follow_up' | 'last_inbound' | 'last_outbound';
  type ViewPreset = { stage: string[]; status: string[]; myDeals: boolean; pinned: boolean; sortKey: SortKey; sortDir: 'asc' | 'desc' };
  // `bookmarked` = the VIEW is bookmarked (🔖, shows in the Bookmarked shortcuts).
  // This is distinct from a client ★ (a per-client sidebar pin, $pinnedClients);
  // `preset.pinned` filters the table down to those pinned clients.
  type SavedView = { key: string; name: string; bookmarked: boolean; builtin: boolean; preset: ViewPreset };

  const STATUS_VALUES = ['Overdue', 'Soon', 'On track', 'Parked'];   // Status chip options
  const emptyPreset = (over: Partial<ViewPreset> = {}): ViewPreset =>
    ({ stage: [], status: [], myDeals: false, pinned: false, sortKey: 'score', sortDir: 'desc', ...over });
  // `name` is display only — `key` is the identity a saved view is stored under,
  // so relabelling these cannot strand anyone's view. Read with get() rather
  // than `$terms`: this runs at component init, and the vocabulary is already
  // in place by then because the shell does not render before the boot call
  // resolves (see App.svelte applyVocabulary).
  const _t = get(terms);
  const BUILTIN_VIEWS: SavedView[] = [
    { key: 'all',             name: `All ${_t.engagement_title?.toLowerCase() ?? 'deal'}s`, bookmarked: true,  builtin: true, preset: emptyPreset() },
    { key: 'my_pipeline',     name: 'Mine',            bookmarked: true,  builtin: true, preset: emptyPreset({ myDeals: true }) },
    { key: 'starred_clients', name: `Starred ${_t.counterparty_plural ?? 'clients'}`, bookmarked: false, builtin: true, preset: emptyPreset({ pinned: true }) },
    { key: 'overdue',         name: 'Overdue',         bookmarked: false, builtin: true, preset: emptyPreset({ status: ['Overdue'], sortKey: 'follow_up', sortDir: 'asc' }) },
    { key: 'up_next',         name: 'Up next',         bookmarked: false, builtin: true, preset: emptyPreset({ status: ['Soon'], sortKey: 'follow_up', sortDir: 'asc' }) },
  ];
  // "All deals" is the one fixed anchor — the permanent "everything / clear all
  // filters" home. It can't be updated, renamed, or deleted. Every OTHER view
  // (Mine, Starred, Overdue, Up next, and custom views) is fully editable.
  const PROTECTED_VIEW_KEYS = new Set(['all']);
  const isProtectedView = (v?: SavedView) => !!v && PROTECTED_VIEW_KEYS.has(v.key);
  // Single source of truth for saved views = the raw view-state KV (per user).
  // `allViews` is derived from it: BUILTIN_VIEWS are seed DEFAULTS, but a KV entry
  // under a builtin's key OVERRIDES it (rename / bookmark), and `{deleted:true}`
  // tombstones it (so a deleted builtin does NOT resurrect on reload). Non-builtin
  // keys with a `name` are custom views. This makes every view editable/deletable
  // without a one-way bulk migration — overrides are written only on edit.
  let rawViewStates = $state<Record<string, any>>({});

  let rows = $state<DashboardRow[]>(_dashInit?.rows ?? []);
  let builtAt = $state(_dashInit?.built_at ?? '');
  let search = $state<string>('');
  let stageFilter = $state<string[]>([]);      // Stage chip selection; [] = All
  let stageSources = $state<Record<string, 'manual' | 'manual_override' | 'ai'>>({});
  let autoStagePending = $state<Record<string, boolean>>({});
  const autoRefreshTimers = new Set<ReturnType<typeof setInterval>>();
  onDestroy(() => { for (const timer of autoRefreshTimers) clearInterval(timer); });
  let statusFilter = $state<string[]>([]);     // Status chip selection; [] = All
  let pinnedOnly = $state(false);              // set by the "Starred clients" view preset (not a chip)
  // Pipeline sort. Part of the current view's preset; edits set the
  // "· modified" flag until saved (see updateActiveView / saveNewView).
  let sortKey = $state<SortKey>('score');
  let sortDir = $state<'asc' | 'desc'>('desc');
  let expandedStages = $state<Set<string>>(new Set(_expInit ?? []));
  let expandedClients = $state<Set<string>>(new Set());
  let signalsMap = $state<Record<string, { items: IntentSignal[]; last_checked: string }>>({});
  let trackingStats = $state<Record<string, { total_opens: number; max_opens: number }>>({});
  let trackingRecent = $state<Record<string, Array<{ subject: string; open_count: number; first_opened: string; last_opened: string }>>>({});
  let expandedSignals = $state<Set<string>>(new Set());
  let expandedTracking = $state<Set<string>>(new Set());
  let scoreTooltip = $state<string | null>(null);
  let myDeals = $state<boolean>(_lastMine);   // first paint uses the last-shown variant
  let editFollowup = $state<string | null>(null);
  let editFollowupDate = $state('');
  let renamingClient = $state<string | null>(null);
  let renameDraft = $state('');
  let renameSaving = $state(false);
  let renameError = $state('');

  let trialDismissed = $state(false);
  let cancelDismissed = $state(false);
  let showUpgrade = $state(false);

  function formatEndsAt(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  let evalCredits = $state<{ used: number; free: number; remaining: number } | null>(null);

  function relativeTime(iso: string): string {
    if (!iso) return '';
    // Ensure UTC interpretation
    const ts = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 0 || diff < 60000) return 'just now';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  async function saveFollowupDate(client: string) {
    if (!editFollowupDate) return;
    await setFollowup(client, editFollowupDate);
    ++loadSeq;  // invalidate any read started before the write
    const row = rows.find(r => r.client === client);
    if (row) row.follow_up = editFollowupDate;
    editFollowup = null;
    editFollowupDate = '';
    await loadDashboard();
  }

  async function saveRename(oldName: string) {
    const target = renameDraft.trim();
    if (!target || target === oldName) {
      renamingClient = null;
      renameDraft = '';
      renameError = '';
      return;
    }
    renameSaving = true;
    renameError = '';
    try {
      const res = await renameClient(oldName, target);
      // Update in-memory row + stages map immediately so the dashboard reflects the change
      const row = rows.find(r => r.client === oldName);
      if (row) row.client = res.new_name;
      await refreshStages();
      renamingClient = null;
      renameDraft = '';
    } catch (e: any) {
      renameError = e?.message || 'Rename failed';
    } finally {
      renameSaving = false;
    }
  }

  // Most-advanced stage FIRST, matching the default Score-desc "hot first" sort.
  // The order lives in STAGE_ORDER so Contacts cannot drift from it; do NOT flip it
  // to the backend's funnel order. Non-Clients appears only when selected in
  // the stage filter, so it does not inflate the default sales pipeline.
  const allStages = STAGE_ORDER;
  const filterStages = [...STAGE_ORDER, 'Non-Clients'];
  const AUTO_COLLAPSE_THRESHOLD = 100;
  const stageEmoji: Record<string, string> = {
    'Prospect': '⚪', 'Top of Funnel': '🔵', 'Engaged': '🟡',
    'Active Deal': '🟠', 'Near Close': '🟣', 'Client': '🟢', 'On Hold': '⏸️', 'Non-Clients': '🚫',
  };
  // If we restored a persisted expansion state, treat it as initialized so the
  // auto-collapse heuristic doesn't override it on load (that was the pop).
  let expansionInitialized = !!_expInit;

  let loadSeq = 0;
  let loadError = $state('');
  async function loadDashboard() {
    const seq = ++loadSeq;
    const wantMine = myDeals;
    try {
      const [d, , sourcesData] = await Promise.all([
        getDashboard(wantMine), refreshStages(), getStageSources().catch(() => null),
      ]);
      if (seq !== loadSeq) return;
      loadError = '';
      if (sourcesData) stageSources = sourcesData;
      evalCredits = d.outbound_eval_credits ?? null;
      // `built_at` tracks poll rebuilds, but other writers can update a cache
      // row independently. Compare the returned rows too, so a fresh follow-up
      // cannot be discarded just because the poll version stayed the same.
      const unchanged = d.built_at && d.built_at === builtAt
        && rows.length > 0 && loadedVariant === wantMine
        && JSON.stringify(d.rows || []) === JSON.stringify(rows);
      if (!unchanged) {
        rows = d.rows || [];
        builtAt = d.built_at || '';
        loadedVariant = wantMine;
        writeCache(DASH_ROWS_CACHE(wantMine), { built_at: builtAt, rows });
        writeCache(LAST_MINE_KEY, wantMine);
      }
      if (!expansionInitialized) {
        const counts: Record<string, number> = {};
        for (const row of rows) {
          const s = $stages[row.client] || '';
          counts[s] = (counts[s] || 0) + 1;
        }
        expandedStages = new Set(
          filterStages.filter(s => s !== 'Non-Clients' && (counts[s] ?? 0) <= AUTO_COLLAPSE_THRESHOLD)
        );
        expansionInitialized = true;
        writeCache(EXPANDED_CACHE, [...expandedStages]);
      }
    } catch (err: any) {
      if (seq === loadSeq) loadError = err?.message || `Could not load ${$terms.counterparty_plural}`;
    }
  }

  onMount(async () => {
    // Cache-first: paint last-known rows instantly (no loading flash).
    hydrateDash(myDeals);
    // Hydrate user-saved custom views from the view-state KV before first paint.
    let rawStates: Record<string, any> = {};
    try {
      const { view_states } = await getDashboardViewStates();
      rawStates = view_states || {};
      rawViewStates = rawStates;
    } catch {}
    // Load rows so we can size-pick the default view.
    await loadDashboard();
    // Smart default for first-time users (nothing stored at all): small
    // pipelines open on "All", larger ones on "Mine" (focused first paint).
    if (Object.keys(rawStates).length === 0) {
      const sellingRows = rows.filter(r => {
        const s = $stages[r.client] || '';
        return s && s !== 'Non-Clients' && s !== 'Prospect' && s !== 'On Hold';
      });
      viewKey = sellingRows.length > 10 ? 'my_pipeline' : 'all';
    }
    // Apply the selected view's preset (builtin or custom).
    const v = allViews.find(x => x.key === viewKey) ?? BUILTIN_VIEWS[0];
    stageFilter = [...v.preset.stage];
    if (stageFilter.includes('Non-Clients')) expandNonClients();
    statusFilter = [...v.preset.status];
    myDeals = v.preset.myDeals;
    pinnedOnly = v.preset.pinned;
    sortKey = v.preset.sortKey;
    sortDir = v.preset.sortDir;
    // my_deals is a backend query param. If the applied preset's owner-variant
    // differs from what we already loaded (e.g. last session was "Mine" so we
    // loaded my-deals rows, but the applied view is "All deals"), paint that
    // variant's cache instantly then refetch — otherwise the table shows the
    // wrong owner-set under the wrong label until the next poll.
    if (loadedVariant !== myDeals) { hydrateDash(myDeals); await loadDashboard(); }
    getIntentSignals().then(s => { signalsMap = s.signals || {}; }).catch(() => {});
    getDashboardTracking().then(t => {
      trackingStats = t.stats || {};
      trackingRecent = t.recent || {};
    }).catch(() => {});
  });

  onMount(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadDashboard().catch(() => {}); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  });

  // Reload when poll completes. untrack the call so this effect depends ONLY on
  // $pollVersion — loadDashboard() reads myDeals synchronously, and without
  // untrack the effect would also track myDeals and double-fetch on Owner toggle
  // (once here, once in toggleMyDeals).
  $effect(() => { if ($pollVersion > 0) { untrack(() => loadDashboard()); } });

  // While a poll is in progress, refresh the dashboard every 3s so the
  // table fills in iteratively as the pipeline writes rows to
  // dashboard_cache — instead of staring at stale data until the build
  // finishes. The pipeline rebuilds the cache every ~10% of clients
  // processed, so 3s is fine-grained enough to see noticeable progress.
  $effect(() => {
    if (!$pollingActive) return;
    const timer = setInterval(() => { loadDashboard().catch(() => {}); }, 3000);
    // Cleanup runs when $pollingActive flips false AND on unmount — the latter
    // was missing before, so the interval kept firing against a dead component.
    return () => clearInterval(timer);
  });

  // NOTE: filter/sort state is no longer auto-saved per view. Views are
  // explicit presets now — edits set the "· modified" flag and persist only
  // when the user hits Update view / Save as new. This removes the old
  // read-and-write $effect, which risks effect_update_depth_exceeded.

  async function toggleMyDeals() {
    myDeals = !myDeals;
    hydrateDash(myDeals);   // paint the other variant's cache instantly
    await loadDashboard();
  }

  function hasFollowUp(followUp: string): boolean {
    return !!followUp && followUp !== '—';
  }

  // Follow-up STATUS — its own axis, independent of the "On Hold" client stage.
  // "Parked" = no active follow-up clock (dead / no date / manually cleared);
  // the backend sends "Parked" directly, legacy rows may still send "—".
  function urgencyBadge(followUp: string, client?: string): { label: string; cls: string } {
    const status = followupStatus(followUp, client ? $stages[client] : '');
    return { label: status.label, cls: `badge-${status.tone}` };
  }

  // Status sort precedence (ascending): on track › overdue › soon › parked.
  const STATUS_RANK: Record<string, number> = {
    'On track': 0, 'Overdue': 1, 'Soon': 2, 'Parked': 3,
  };

  // `includeStage=false` applies every filter EXCEPT the stage chip — used by
  // stageCounts so each stage's count reflects the other active filters (search /
  // status / pinned), i.e. exactly what selecting that stage would show.
  function filteredRows(includeStage = true): DashboardRow[] {
    let r = rows.filter(row => row.client !== 'Non-Clients');
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(row => row.client.toLowerCase().includes(q));
    }
    if (pinnedOnly) r = r.filter(row => $pinnedClients.includes(row.client));
    if (includeStage) {
      r = r.filter(row => {
        const stage = $stages[row.client] || '';
        if (stage === 'Non-Clients' && !stageFilter.includes('Non-Clients')) return false;
        return stageFilter.length === 0 || stageFilter.includes(stage);
      });
    }
    if (statusFilter.length) r = r.filter(row => statusFilter.includes(urgencyBadge(row.follow_up, row.client).label));
    return r;
  }

  function groupByStage(rows: DashboardRow[]): Record<string, DashboardRow[]> {
    const groups: Record<string, DashboardRow[]> = {};
    for (const row of rows) {
      const s = $stages[row.client] || '— unset —';
      (groups[s] ??= []).push(row);
    }
    // Sort each group by the current view's sort. Prospect rows have no
    // score; their default fallback (alphabetical) is just the natural
    // 'client' sort, so the same comparator works there too.
    for (const key of Object.keys(groups)) {
      groups[key].sort(rowComparator);
    }
    return groups;
  }

  // Compare two rows under the active sortKey/sortDir. String keys use
  // localeCompare; date strings are lexicographically comparable
  // (YYYY-MM-DD). Empty / unset → sentinel that sorts last so "no data"
  // doesn't pollute the top of the list when ascending.
  // Live-adjusted score shown in the Score pill AND used to sort the Score
  // column (they must match — that's why both call this).
  //   • Unscored sentinel is `score < 0` → return it so the pill hides. (The old
  //     code gated on `(ai_score ?? score) >= 0`, but ai_score is always set to 0,
  //     so a `score:-1` prospect rendered a spurious "0" pill.)
  //   • Display value is `ai_score` when the AI has actually scored the lead
  //     (== total `score` on ~95% of rows), else fall back to total `score`.
  //     The backend sets ai_score=0 (not null) for un-AI-scored leads, so a plain
  //     `ai_score ?? score` never falls through — a rule-scored lead (score>0,
  //     ai_score=0) would collapse to 0 and sink to the bottom. Gate on `> 0`.
  //   • Un-replied Top-of-Funnel prospects who've opened our mail get a small
  //     bonus. `!row.last_inbound` is the reliable "never replied" signal — the
  //     old `!row.has_inbound` referenced a field present on only ~4% of rows,
  //     so the guard was effectively always-true.
  function liveScoreOf(row: DashboardRow): number {
    const score = row.score ?? -1;
    if (score < 0) return score;
    const base = (row.ai_score ?? 0) > 0 ? row.ai_score! : score;
    const ts = trackingStats[row.client];
    const bonus = ($stages[row.client] === 'Top of Funnel' && ts?.max_opens && !row.last_inbound)
      ? (ts.max_opens >= 3 ? 7 : ts.max_opens === 2 ? 5 : ts.max_opens >= 1 ? 3 : 0)
      : 0;
    return Math.min(100, base + bonus);
  }
  function rowComparator(a: DashboardRow, b: DashboardRow): number {
    const dir = sortDir === 'asc' ? 1 : -1;
    const stageOf = (r: DashboardRow) => ($stages[r.client] || '');
    const statusOf = (r: DashboardRow) => {
      const u = urgencyBadge(r.follow_up, r.client);
      return u.label;
    };
    const dateField = (r: DashboardRow, k: 'follow_up' | 'last_inbound' | 'last_outbound') => {
      const v = (r as any)[k] || '';
      return (v === '—' || !v) ? (sortDir === 'asc' ? '9999-99-99' : '') : v;
    };
    switch (sortKey) {
      case 'client': return a.client.localeCompare(b.client) * dir;
      case 'stage':  return stageOf(a).localeCompare(stageOf(b)) * dir;
      case 'status': return ((STATUS_RANK[statusOf(a)] ?? 9) - (STATUS_RANK[statusOf(b)] ?? 9)) * dir;
      case 'score':  return (liveScoreOf(b) - liveScoreOf(a)) * (dir === 1 ? -1 : 1);
      case 'follow_up':    return dateField(a, 'follow_up').localeCompare(dateField(b, 'follow_up')) * dir;
      case 'last_inbound': return dateField(a, 'last_inbound').localeCompare(dateField(b, 'last_inbound')) * dir;
      case 'last_outbound':return dateField(a, 'last_outbound').localeCompare(dateField(b, 'last_outbound')) * dir;
    }
    return 0;
  }

  // Score pills are colored by magnitude — a deliberate exception to the
  // "hue reserved for Status only" rule (see DECISIONS): the band color
  // comes from scoreColorFn (strong ≥75 / moderate ≥50 / early ≥25 / cold). A very
  // low score (<25) stays a muted grey chip, and 0 is a transparent outline.
  const scoreBadgeStyle = (score: number) => sharedScoreBadgeStyle(score, $colorScheme);

  function toggleSignals(client: string) {
    const next = new Set(expandedSignals);
    if (next.has(client)) next.delete(client); else next.add(client);
    expandedSignals = next;
  }

  function toggleStageExpand(stageName: string) {
    const next = new Set(expandedStages);
    if (next.has(stageName)) next.delete(stageName); else next.add(stageName);
    expandedStages = next;
    writeCache(EXPANDED_CACHE, [...next]);   // persist so it restores on return
  }

  // ── Chunked rendering: fast initial render, explicit Load More ──
  const INITIAL_CHUNK = 1000;
  const CHUNK_INCREMENT = 1000;
  let renderLimits = $state<Record<string, number>>({});
  function loadMore(stage: string) {
    renderLimits = { ...renderLimits, [stage]: (renderLimits[stage] ?? INITIAL_CHUNK) + CHUNK_INCREMENT };
  }

  function toggleClientExpand(client: string) {
    const next = new Set(expandedClients);
    if (next.has(client)) next.delete(client); else next.add(client);
    expandedClients = next;
  }

  // Per-client stage error, surfaced next to the select (same idiom as renameError).
  let stageError = $state<Record<string, string>>({});

  async function handleStageChange(client: string, newStage: string) {
    try {
      await setStage(client, newStage);
      setClientStage(client, newStage);
      stageSources = { ...stageSources, [client]: 'manual_override' };
      stageError = { ...stageError, [client]: '' };
    } catch (err: any) {
      // Without this the PUT rejected silently and the select kept showing the
      // stage the user picked, which read as success.
      stageError = { ...stageError, [client]: err?.message || 'Could not change the stage' };
    }
    // The server decides what the stage now is — it may reject it, or hide it
    // (GET /stages omits another user's Non-Clients), so never assume `newStage`.
    await refreshStages();
    getStageSources().then(s => { stageSources = s; }).catch(() => {});
  }

  async function resumeAutoStage(client: string) {
    autoStagePending = { ...autoStagePending, [client]: true };
    try {
      const beforeBuild = builtAt;
      await resumeAutomaticStage(client);
      stageSources = { ...stageSources, [client]: 'ai' };
      stageError = { ...stageError, [client]: '' };
      // The backend rebuild runs after its response. Refresh until that build
      // changes the cache version, with a short bound if it fails.
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        await loadDashboard().catch(() => {});
        if (builtAt !== beforeBuild || attempts >= 15) {
          clearInterval(timer);
          autoRefreshTimers.delete(timer);
        }
      }, 2000);
      autoRefreshTimers.add(timer);
    } catch (err: any) {
      stageError = { ...stageError, [client]: err?.message || 'Could not resume automatic stage updates' };
    } finally {
      autoStagePending = { ...autoStagePending, [client]: false };
    }
  }

  const legacyAutoStage = (client: string) =>
    stageSources[client] === 'manual' && !['Non-Clients', 'On Hold', 'Client'].includes($stages[client] || '');

  async function lockLegacyStage(client: string) {
    await handleStageChange(client, $stages[client]);
  }

  // Toggle a value in the Stage or Status chip multi-select ([] = All).
  function toggleFilter(axis: 'stage' | 'status', value: string) {
    const cur = axis === 'stage' ? stageFilter : statusFilter;
    const next = cur.includes(value) ? cur.filter(x => x !== value) : [...cur, value];
    if (axis === 'stage') {
      stageFilter = next;
      if (value === 'Non-Clients' && next.includes(value)) expandNonClients();
    } else statusFilter = next;
  }
  // Clear = drop every filter AND jump back to the All-deals view.
  function clearFilters() {
    search = '';
    applyView(BUILTIN_VIEWS[0]);   // 'all' — resets stage/status/pinned/owner + sort
  }

  function expandNonClients() {
    expandedStages = new Set([...expandedStages, 'Non-Clients']);
    writeCache(EXPANDED_CACHE, [...expandedStages]);
  }

  async function showNonClients() {
    search = '';
    await applyView(BUILTIN_VIEWS[0]);
    stageFilter = ['Non-Clients'];
    expandNonClients();
  }

  // ── Saved views (builtin presets + user-saved custom views) ──────────
  let viewKey = $state<string>('all');
  let viewDropdownOpen = $state(false);
  let openFilterDD = $state<'stage' | 'status' | 'owner' | null>(null);

  const allViews = $derived<SavedView[]>([...mergedBuiltins(rawViewStates), ...customViewsFrom(rawViewStates)]);
  function activeView(): SavedView | undefined { return allViews.find(v => v.key === viewKey); }

  function currentPreset(): ViewPreset {
    return { stage: [...stageFilter], status: [...statusFilter], myDeals, pinned: pinnedOnly, sortKey, sortDir };
  }
  function presetEq(a: ViewPreset, b: ViewPreset): boolean {
    const setEq = (x: string[], y: string[]) =>
      x.length === y.length && [...x].sort().join('|') === [...y].sort().join('|');
    return setEq(a.stage, b.stage) && setEq(a.status, b.status)
      && a.myDeals === b.myDeals && a.pinned === b.pinned && a.sortKey === b.sortKey && a.sortDir === b.sortDir;
  }
  // "· modified" flag — current filters/sort differ from the selected view.
  const isDirty = $derived.by(() => {
    const v = allViews.find(x => x.key === viewKey);
    return v ? !presetEq(currentPreset(), v.preset) : true;
  });
  const hasActiveFilters = $derived(
    stageFilter.length > 0 || statusFilter.length > 0 || search.length > 0 || myDeals || pinnedOnly
  );
  const stageLbl = $derived(
    stageFilter.length === 0 ? 'All' : stageFilter.length === 1 ? $stageLabel(stageFilter[0]) : stageFilter.length + ' selected'
  );
  const statusLbl = $derived(
    statusFilter.length === 0 ? 'All' : statusFilter.length === 1 ? statusFilter[0] : statusFilter.length + ' selected'
  );
  // Client count per stage (over the loaded set) — shown in the Stage filter so an
  // empty stage reads as "genuinely empty", not broken, and the filter lines up
  // with the table's group counts.
  const stageCounts = $derived.by(() => {
    const c: Record<string, number> = {};
    for (const row of filteredRows(false)) { const s = $stages[row.client] || ''; c[s] = (c[s] || 0) + 1; }
    return c;
  });
  const parkedCount = $derived(Object.entries($stages).filter(([client, stage]) => client !== 'Non-Clients' && stage === 'Non-Clients').length);

  async function applyView(v: SavedView) {
    viewKey = v.key;
    viewDropdownOpen = false;
    const wasMine = myDeals;
    stageFilter = [...v.preset.stage];
    if (stageFilter.includes('Non-Clients')) expandNonClients();
    statusFilter = [...v.preset.status];
    myDeals = v.preset.myDeals;
    pinnedOnly = v.preset.pinned;
    sortKey = v.preset.sortKey;
    sortDir = v.preset.sortDir;
    if (myDeals !== wasMine) { hydrateDash(myDeals); await loadDashboard(); }
  }

  // Client-count preview for a view. Owner/my_deals can't be computed without
  // per-row owner data, so it's ignored here — the count is over the loaded set.
  function viewCount(v: SavedView): number {
    return rows.filter(row => {
      const stage = $stages[row.client] || '';
      if (stage === 'Non-Clients' && !v.preset.stage.includes('Non-Clients')) return false;
      if (v.preset.pinned && !$pinnedClients.includes(row.client)) return false;
      if (v.preset.stage.length && !v.preset.stage.includes(stage)) return false;
      if (v.preset.status.length && !v.preset.status.includes(urgencyBadge(row.follow_up, row.client).label)) return false;
      return true;
    }).length;
  }

  function parsePreset(s: any): ViewPreset {
    return {
      stage: Array.isArray(s.stage) ? s.stage : [],
      status: Array.isArray(s.status) ? s.status : [],
      myDeals: !!s.myDeals,
      pinned: !!s.pinned,
      sortKey: (s.sortKey as SortKey) ?? 'score',
      sortDir: (s.sortDir as 'asc' | 'desc') ?? 'desc',
    };
  }
  const hasPresetKeys = (s: any) =>
    'stage' in s || 'status' in s || 'sortKey' in s || 'myDeals' in s || 'pinned' in s;
  // Builtins with per-user overrides applied; tombstoned ones dropped.
  // Only a REAL override counts — my writes always carry `name`, tombstones carry
  // `deleted`. LEGACY auto-saved filter states (no name/deleted, old `stageFilter`
  // shape) must be ignored, exactly as the old code did, or they'd corrupt the
  // builtin's preset (e.g. give "Mine" a stale last_inbound sort).
  function mergedBuiltins(states: Record<string, any>): SavedView[] {
    return BUILTIN_VIEWS.flatMap(b => {
      const o = states[b.key];
      const isOverride = o && typeof o === 'object' && (o.name != null || o.deleted);
      if (!isOverride) return [b];
      if (o.deleted) return [];
      return [{
        key: b.key, builtin: true,
        name: o.name ?? b.name,
        bookmarked: o.bookmarked ?? b.bookmarked,
        preset: hasPresetKeys(o) ? parsePreset(o) : b.preset,
      }];
    });
  }
  // Non-builtin KV entries with a `name` (and not tombstoned) = custom views.
  function customViewsFrom(states: Record<string, any>): SavedView[] {
    const out: SavedView[] = [];
    for (const [key, st] of Object.entries(states || {})) {
      const s = st as any;
      if (key.startsWith('__')) continue;
      if (BUILTIN_VIEWS.some(b => b.key === key)) continue;
      if (!s || typeof s !== 'object' || !s.name || s.deleted) continue;
      out.push({ key, name: s.name, bookmarked: !!(s.bookmarked ?? s.starred), builtin: false, preset: parsePreset(s) });
    }
    return out;
  }
  // Optimistic local write + server persist (both keyed on view_key).
  function setViewState(key: string, state: Record<string, any>) {
    rawViewStates = { ...rawViewStates, [key]: state };
    saveDashboardViewState(key, state).catch(() => {});
  }
  function removeViewState(key: string) {
    const { [key]: _drop, ...rest } = rawViewStates;
    rawViewStates = rest;
    deleteDashboardViewState(key).catch(() => {});
  }
  // Full override payload for a view (rename/bookmark carry the whole preset so
  // a builtin override is self-contained and round-trips).
  const viewPayload = (v: SavedView, over: Partial<{ name: string; bookmarked: boolean }> = {}) =>
    ({ name: over.name ?? v.name, bookmarked: over.bookmarked ?? v.bookmarked, ...v.preset });

  async function toggleViewBookmark(v: SavedView, e: Event) {
    e.stopPropagation();
    setViewState(v.key, viewPayload(v, { bookmarked: !v.bookmarked }));
  }
  function renameView(v: SavedView, newName: string) {
    if (isProtectedView(v)) return;
    const name = newName.trim();
    if (!name || name === v.name) return;
    setViewState(v.key, viewPayload(v, { name }));
  }
  async function deleteView(v: SavedView, e: Event) {
    e.stopPropagation();
    if (isProtectedView(v)) return;   // "All deals" is the fixed anchor
    if (!confirm(`Delete saved view “${v.name}”?`)) return;
    // Builtin → tombstone (won't resurrect on reload); custom → real delete.
    if (v.builtin) setViewState(v.key, { name: v.name, deleted: true });
    else removeViewState(v.key);
    // If the deleted view was active, fall back to the first remaining view.
    if (viewKey === v.key) {
      const next = allViews.find(x => x.key !== v.key) ?? BUILTIN_VIEWS[0];
      applyView(next);
    }
  }

  // ── Save view ──
  // When a view is "· modified", the bar shows both actions explicitly:
  //   Update view  → overwrite the active custom view in place (instant, no modal)
  //   Save as new  → name a brand-new view (the only option on a built-in)
  let saveModalOpen = $state(false);
  let saveName = $state('');
  // Inline rename in the view dropdown: the hovered row's name becomes an input.
  let editingViewKey = $state<string | null>(null);
  function focusSelect(node: HTMLInputElement) { node.focus(); node.select(); }
  function updateActiveView() {
    const v = activeView();
    if (!v || isProtectedView(v)) return;   // "All deals" can't be overwritten
    // Works for the other built-ins too — writes a per-user override of the preset
    // (the overlay model). "Save as new" is the alternative on the locked anchors.
    setViewState(v.key, { name: v.name, bookmarked: v.bookmarked, ...currentPreset() });
  }
  function openSaveAsNew() { saveName = ''; saveModalOpen = true; }
  function saveNewView() {
    const name = saveName.trim() || 'Untitled view';
    const key = 'custom:' + Date.now();
    setViewState(key, { name, bookmarked: false, ...currentPreset() });
    viewKey = key;
    saveModalOpen = false;
  }

  // Close open dropdowns on any click outside their wrapper.
  function onWindowClick(e: MouseEvent) {
    const t = e.target as HTMLElement;
    if (openFilterDD && !t.closest('.fchip-wrap')) openFilterDD = null;
    if (viewDropdownOpen && !t.closest('.dash-view-bar')) viewDropdownOpen = false;
  }

  // Click a sortable column header. Same key twice → toggle direction;
  // a different key → switch to that column with its sensible default
  // direction (most recent / highest first for dates + score, A→Z for
  // strings, ascending for follow_up since "next due" reads more naturally
  // at the top). Sort is part of the view preset — changing it sets "· modified".
  function sortBy(k: SortKey) {
    if (sortKey === k) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = k;
      sortDir = (k === 'follow_up' || k === 'client' || k === 'stage' || k === 'status') ? 'asc' : 'desc';
    }
  }
  // Sort arrow for a column header. EVERY sortable column shows a small faint
  // grey ▼ (down) so users can tell it's sortable. The ACTIVE column's arrow
  // grows and turns accent-orange (see `.sort-ind.active` in CSS), and flips to
  // ▲ when the sort is ascending so the direction is still legible.
  function sortIndicator(k: SortKey): string {
    if (sortKey !== k) return '▼';
    return sortDir === 'asc' ? '▲' : '▼';
  }
  const currentViewName = $derived(allViews.find(v => v.key === viewKey)?.name ?? 'All deals');

  // ── Pin / unpin (sidebar quick-jump) ──────────────────────────────
  async function togglePin(client: string, event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();
    const isPinned = $pinnedClients.includes(client);
    try {
      if (isPinned) {
        await unpinClient(client);
        $pinnedClients = $pinnedClients.filter(c => c !== client);
      } else {
        await pinClient(client);
        $pinnedClients = [...$pinnedClients, client];
      }
    } catch (e) {
      console.error('pin toggle failed', e);
    }
  }

  // Stage color palette — used for the colored dot in group headers and
  // pinned-clients list. Matches the dashboard mockup.
  // (Per-row Stage pill removed — stage now shows only in the group header.
  //  DESIGN-SYSTEM.md §6 column order drops the per-row Stage column.)

  // Activity: "Nd ago" string from the most-recent thread date for a client.
  function activityAgo(lastContact: string): string {
    if (!lastContact || lastContact === '—') return '—';
    const then = new Date(lastContact + 'T00:00:00Z').getTime();
    const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
    if (days === 0) return '0d ago';
    if (days === 1) return '1d ago';
    return `${days}d ago`;
  }
</script>

<div style="display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;">
  <div class="page-title">Deal Dashboard</div>
  <div style="display:flex; flex-direction:row; gap:8px; align-items:center;">
    <SyncButton />
    <button
      onclick={() => showBugModal = true}
      title="Report a bug — emails daniel@dealton.ai"
      aria-label="Report a bug"
      style="font-size:14px; line-height:1; padding:6px 10px; border-radius:var(--radius); border:1px solid var(--border); cursor:pointer; background:transparent; color:var(--text-muted);"
    >
      🐞
    </button>
  </div>
</div>

<BugReportModal bind:open={showBugModal} />
<div class="page-subtitle">
  {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
  {#if builtAt}
    <span style="margin-left: 12px;" class="text-dim">Last build: {new Date(builtAt.endsWith('Z') || builtAt.includes('+') ? builtAt : builtAt + 'Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} {new Date(builtAt.endsWith('Z') || builtAt.includes('+') ? builtAt : builtAt + 'Z').toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
  {/if}
  {#if evalCredits !== null}
    <span style="margin-left: 12px;" class="text-dim" title="Outbound evaluation credits remaining this month">⚡ {evalCredits.remaining}/{evalCredits.free}</span>
  {/if}
</div>

<!-- Trial banner -->
{#if $userTier === 0 && $userDaysRemaining !== null && !trialDismissed}
  <div class="trial-banner">
    <span>Trial: {$userDaysRemaining} day{$userDaysRemaining === 1 ? '' : 's'} remaining</span>
    <button class="trial-upgrade" onclick={() => showUpgrade = true}>Upgrade</button>
    <button class="trial-dismiss" onclick={() => trialDismissed = true}>×</button>
  </div>
{/if}

<!-- Subscription-ending banner — surfaces a scheduled cancellation -->
{#if $userTier >= 2 && $subscriptionEndsAt && !cancelDismissed}
  <div class="trial-banner">
    <span>
      Subscription ends {formatEndsAt($subscriptionEndsAt)}. You'll lose access after that date.
      {#if !$isAdmin}<span style="opacity:0.85;">— ask your admin to keep it.</span>{/if}
    </span>
    {#if $isAdmin}
      <button class="trial-upgrade" onclick={async () => {
        try {
          const r = await resumeSubscription();
          $subscriptionEndsAt = r.ends_at ?? '';
        } catch (e: any) {
          alert(e?.message || 'Could not resume subscription');
        }
      }}>Resume Subscription</button>
    {/if}
    <button class="trial-dismiss" onclick={() => cancelDismissed = true}>×</button>
  </div>
{/if}


<!-- Scheduled emails widget moved to Today (Going out soon section,
     between Briefing and Calls). Dashboard's Pipeline view stays focused
     on the client list. -->


<svelte:window onclick={onWindowClick} />

<!-- Saved-view bar: view dropdown · modified · N clients · Save current view -->
<div class="dash-view-bar">
  <div class="vdt" role="button" tabindex="0"
    onclick={() => viewDropdownOpen = !viewDropdownOpen}
    onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') viewDropdownOpen = !viewDropdownOpen; }}>
    {#if activeView()?.bookmarked}<span class="vdt-bm" aria-label="Bookmarked"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg></span>{/if}
    <span class="vdt-name">{currentViewName}</span>
    <span class="vdt-arrow">▼</span>
  </div>
  {#if isDirty}<span class="vdt-dirty">· modified</span>{/if}
  <span class="count-label"><b>{filteredRows().length}</b> {$terms.counterparty_plural}</span>
  <div class="dash-view-spacer"></div>
  {#if isDirty}
    {#if !isProtectedView(activeView())}
      <button class="dash-save-btn" onclick={updateActiveView}
        title="Overwrite “{activeView()?.name}” with the current filters + sort">Update view</button>
    {/if}
    <button class="dash-save-btn ghost" onclick={openSaveAsNew}>Save as new</button>
  {/if}
  {#if viewDropdownOpen}
    <div class="vdm" role="menu">
      <div class="vdm-section-label">Bookmarked</div>
      {#each allViews.filter(v => v.bookmarked) as v (v.key)}
        {@render viewRow(v)}
      {/each}
      <div class="vdm-divider"></div>
      <div class="vdm-section-label">All views</div>
      {#each allViews.filter(v => !v.bookmarked) as v (v.key)}
        {@render viewRow(v)}
      {/each}
    </div>
  {/if}
</div>

<!-- One dropdown row: minimal bookmark toggle · name (inline-editable) · count ·
     hover rename/delete. Rename + delete happen in place — no separate modal. -->
{#snippet viewRow(v: SavedView)}
  <div class="vdm-item" class:active={viewKey === v.key} class:editing={editingViewKey === v.key}
    role="menuitem" tabindex="0"
    onclick={() => { if (editingViewKey !== v.key) applyView(v); }}
    onkeydown={(e) => { if (editingViewKey !== v.key && (e.key === 'Enter' || e.key === ' ')) applyView(v); }}>
    <button class="vdm-bm" class:on={v.bookmarked} type="button" aria-label="Toggle bookmark"
      title={v.bookmarked ? 'Remove bookmark' : 'Bookmark (show in shortcuts)'}
      onclick={(e) => toggleViewBookmark(v, e)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
    </button>
    {#if editingViewKey === v.key}
      <input class="vdm-edit" value={v.name} aria-label="Rename view" use:focusSelect
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { renameView(v, (e.currentTarget as HTMLInputElement).value); editingViewKey = null; }
          else if (e.key === 'Escape') editingViewKey = null;
        }}
        onblur={(e) => { renameView(v, (e.currentTarget as HTMLInputElement).value); editingViewKey = null; }} />
    {:else}
      <span class="vdm-name">{v.name}</span>
      {#if !isProtectedView(v)}
        <button class="vdm-act vdm-rename" title="Rename view" aria-label="Rename view"
          onclick={(e) => { e.stopPropagation(); editingViewKey = v.key; }}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
        </button>
        <button class="vdm-act vdm-del" title="Delete view" aria-label="Delete view"
          onclick={(e) => deleteView(v, e)}>✕</button>
      {/if}
      <span class="vdm-count">{viewCount(v)}</span>
    {/if}
  </div>
{/snippet}

<!-- Filter toolbar: search + Stage / Status / Owner chip-dropdowns + Clear -->
<div class="page-toolbar">
  <input class="page-toolbar-search" bind:value={search} placeholder="Search clients…" aria-label="Search clients" />
  <div class="fchip-wrap">
    <button class="fchip" class:set={stageFilter.length > 0}
      onclick={() => openFilterDD = openFilterDD === 'stage' ? null : 'stage'}>
      Stage <span class="fchip-v">{stageLbl}</span> <span class="fchip-ar">▾</span>
    </button>
    {#if openFilterDD === 'stage'}
      <div class="fchip-dd" role="menu">
        {#each filterStages as opt}
          {@const n = stageCounts[opt] ?? 0}
          <button class="fchip-opt" class:on={stageFilter.includes(opt)} class:empty={n === 0}
            onclick={() => toggleFilter('stage', opt)}>
            <span class="fchip-check">✓</span>{$stageLabel(opt)}<span class="fchip-count">{n}</span>
          </button>
        {/each}
      </div>
    {/if}
  </div>
  <div class="fchip-wrap">
    <button class="fchip" class:set={statusFilter.length > 0}
      onclick={() => openFilterDD = openFilterDD === 'status' ? null : 'status'}>
      Status <span class="fchip-v">{statusLbl}</span> <span class="fchip-ar">▾</span>
    </button>
    {#if openFilterDD === 'status'}
      <div class="fchip-dd" role="menu">
        {#each STATUS_VALUES as opt}
          <button class="fchip-opt" class:on={statusFilter.includes(opt)} onclick={() => toggleFilter('status', opt)}>
            <span class="fchip-check">✓</span>{opt}
          </button>
        {/each}
      </div>
    {/if}
  </div>
  <div class="fchip-wrap">
    <button class="fchip" class:set={myDeals}
      onclick={() => openFilterDD = openFilterDD === 'owner' ? null : 'owner'}>
      Owner <span class="fchip-v">{myDeals ? 'Me' : 'All'}</span> <span class="fchip-ar">▾</span>
    </button>
    {#if openFilterDD === 'owner'}
      <div class="fchip-dd" role="menu">
        <button class="fchip-opt" class:on={myDeals} onclick={() => { toggleMyDeals(); openFilterDD = null; }}>
          <span class="fchip-check">✓</span>Me
        </button>
      </div>
    {/if}
  </div>
  {#if hasActiveFilters}
    <button class="fchip-clear" onclick={clearFilters}>Clear ✕</button>
  {/if}
</div>

<!-- Save-current-view modal -->
{#if saveModalOpen}
  <div class="dash-modal-bg" role="presentation"
    onclick={(e) => { if (e.target === e.currentTarget) saveModalOpen = false; }}>
    <div class="dash-modal">
      <h3>Save as new view</h3>
      <input class="dash-modal-input" bind:value={saveName} placeholder="View name…"
        onkeydown={(e) => { if (e.key === 'Enter') saveNewView(); }} />
      <div class="dash-modal-row">
        <button class="dash-mbtn" onclick={() => saveModalOpen = false}>Cancel</button>
        <button class="dash-mbtn primary" onclick={saveNewView}>Save</button>
      </div>
    </div>
  </div>
{/if}

{#if loadError}
  <div class="empty-state" role="status">Could not refresh {$terms.counterparty_plural}. <button type="button" onclick={loadDashboard}>Retry</button></div>
{/if}
{#if loadedVariant === null}
  {#if !loadError}<div class="empty-state">Loading {$terms.counterparty_plural}…</div>{/if}
{:else if rows.length === 0}
  <div class="empty-state">
    <div class="empty-state-icon">👋</div>
    <div class="empty-state-text">Welcome! Click Sync to import your emails.</div>
  </div>
{:else}
  {@const fRows = filteredRows()}
  {@const grouped = groupByStage(fRows)}
  <div class="data-table-wrap">
  <table class="data-table">
    <thead>
      <tr>
        <th style="width:20px;"></th>
        <th class="center th-sort" style="width:60px;" onclick={() => sortBy('score')}>Score<span class="sort-ind" class:active={sortKey === 'score'}>{sortIndicator('score')}</span></th>
        <th class="th-sort" onclick={() => sortBy('client')}>{$terms.counterparty_title}<span class="sort-ind" class:active={sortKey === 'client'}>{sortIndicator('client')}</span></th>
        <th class="col-status th-sort" style="width:96px;" onclick={() => sortBy('status')}>Status<span class="sort-ind" class:active={sortKey === 'status'}>{sortIndicator('status')}</span></th>
        <th class="th-sort" style="width:110px;" onclick={() => sortBy('follow_up')}>Follow-up<span class="sort-ind" class:active={sortKey === 'follow_up'}>{sortIndicator('follow_up')}</span></th>
        <th class="num th-sort col-io" style="width:90px;" onclick={() => sortBy('last_inbound')}>Inbound<span class="sort-ind" class:active={sortKey === 'last_inbound'}>{sortIndicator('last_inbound')}</span></th>
        <th class="num th-sort col-io" style="width:90px;" onclick={() => sortBy('last_outbound')}>Outbound<span class="sort-ind" class:active={sortKey === 'last_outbound'}>{sortIndicator('last_outbound')}</span></th>
      </tr>
    </thead>
    <tbody>
    {#each [...allStages, 'Non-Clients', '— unset —'] as stageName}
      {#if grouped[stageName]?.length}
        <tr class="group-row"
          role="button" tabindex="0"
          onclick={() => toggleStageExpand(stageName)}
          onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleStageExpand(stageName)}
        >
          <td colspan="7">
            <div class="group-header">
              <span class="group-arrow">{expandedStages.has(stageName) ? '▼' : '▶'}</span>
              <span class="stage-dot" style="background:{stageColor(stageName)};"></span>
              <span class="group-name">{$stageLabel(stageName)}</span>
              <span class="group-meta">{grouped[stageName].length} {grouped[stageName].length === 1 ? $terms.counterparty : $terms.counterparty_plural}{!expandedStages.has(stageName) ? ' · collapsed' : ''}</span>
            </div>
          </td>
        </tr>
        {#if expandedStages.has(stageName)}
          {@const _limit = renderLimits[stageName] ?? INITIAL_CHUNK}
          {@const _visible = grouped[stageName].slice(0, _limit)}
            {#each _visible as row}
              {@const urg = urgencyBadge(row.follow_up, row.client)}
              {@const clientSignals = signalsMap[row.client]?.items || []}
              {@const liveScore = liveScoreOf(row)}
              {@const isPinned = $pinnedClients.includes(row.client)}
              {@const isExpanded = expandedClients.has(row.client)}
              <tr class="data-row" class:open={isExpanded}
                role="button" tabindex="0"
                onclick={() => toggleClientExpand(row.client)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleClientExpand(row.client)}
              >
                <td class="cell-pin">
                  <button class="star-btn" class:on={isPinned}
                    title={isPinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
                    onclick={(e) => togglePin(row.client, e)}
                  >{isPinned ? '★' : '☆'}</button>
                </td>
                <td class="cell-score center">
                  {#if liveScore >= 0 && $stages[row.client] !== 'Non-Clients'}
                    <span class="score-num" style="{scoreBadgeStyle(liveScore)}">{liveScore}</span>
                  {/if}
                </td>
                <td class="cell-client">
                  <span class="client-cell-name">{row.client.replace(/_/g, ' ')}</span>
                </td>
                <td class="cell-status col-status">
                  <span class="status-badge-new status-{urg.cls.replace('badge-','')}">{urg.label}</span>
                </td>
                <td class="cell-date">{$stages[row.client] === 'Non-Clients' ? '—' : row.follow_up}</td>
                <td class="cell-num num col-io">{activityAgo(row.last_inbound || '')}</td>
                <td class="cell-num num col-io">{activityAgo(row.last_outbound || '')}</td>
              </tr>
              {#if isExpanded}
                <tr class="detail-row">
                  <td colspan="7">
                    <div class="row-detail">
                      <div class="row-detail-main">
                        <div class="md-content text-sm">
                          {@html renderSafeMarkdown(row.summary || '')}
                        </div>
                        {#if clientSignals.length > 0}
                          <div class="row-detail-sub">
                            <div class="text-xs text-dim" style="font-weight:600; margin-bottom:6px;">News signals</div>
                            {#each clientSignals as signal}
                              <div class="signal-row">
                                <span class="text-xs text-dim">{signal.date}</span>
                                <a href={signal.url} target="_blank" class="signal-link">{signal.title}</a>
                                <span class="text-xs text-dim">{signal.source}</span>
                              </div>
                            {/each}
                          </div>
                        {/if}
                        {#if trackingStats[row.client]?.total_opens > 0 && trackingRecent[row.client]?.length}
                          <div class="row-detail-sub">
                            <div class="text-xs text-dim" style="font-weight:600; margin-bottom:6px;">Email opens ({trackingStats[row.client].total_opens})</div>
                            {#each trackingRecent[row.client] as tr}
                              <div class="signal-row" style="gap:10px;">
                                <span style="font-size:11px; min-width:40px; color:{tr.open_count > 1 ? 'var(--text)' : 'var(--text-muted)'}; font-weight:600;">
                                  {tr.open_count}x
                                </span>
                                <span class="text-xs truncate" style="flex:1;">{tr.subject}</span>
                                <span class="text-xs text-dim">last {tr.last_opened?.slice(5, 16).replace('T', ' ')}</span>
                              </div>
                            {/each}
                          </div>
                        {/if}
                        {#if row.ai_reasoning}
                          <div class="row-detail-sub">
                            <div class="text-xs text-dim" style="font-weight:600; margin-bottom:6px;">{$stages[row.client] === 'Non-Clients' ? 'Historical score reasoning' : 'Score reasoning'}</div>
                            <div class="text-sm">{row.ai_reasoning}</div>
                          </div>
                        {/if}
                      </div>
                      <div class="row-detail-side">
                        <label class="text-xs text-dim" for="stage-{row.client}">Stage</label>
                        <select
                          id="stage-{row.client}"
                          value={$stages[row.client] || ''}
                          onchange={(e) => handleStageChange(row.client, (e.target as HTMLSelectElement).value)}
                        >
                          <!-- 🔴 `value` is explicit. Without it an <option>'s value IS
                               its text, so relabelling the text would silently start
                               writing IR labels into client_stages. -->
                          <option value="" disabled hidden>Choose stage</option>
                          {#each allStages as s}<option value={s}>{$stageLabel(s)}</option>{/each}
                          <option value="Non-Clients">{$stageLabel('Non-Clients')}</option>
                        </select>
                        {#if stageSources[row.client]}
                          <div class="text-xs text-dim" style="margin-top:4px;">
                            {legacyAutoStage(row.client) ? 'Legacy stage · automatic updates' : stageSources[row.client] === 'ai' ? 'Automatic stage' : 'Manual stage'}
                          </div>
                        {/if}
                        {#if legacyAutoStage(row.client)}
                          <button class="row-detail-edit-btn" onclick={() => lockLegacyStage(row.client)}>
                            Keep this stage manual
                          </button>
                        {:else if stageSources[row.client] === 'manual' || stageSources[row.client] === 'manual_override'}
                          <button class="row-detail-edit-btn" disabled={autoStagePending[row.client]}
                            onclick={() => resumeAutoStage(row.client)}>
                            {autoStagePending[row.client] ? 'Updating…' : 'Resume automatic stage updates'}
                          </button>
                        {/if}
                        {#if stageError[row.client]}
                          <span style="color:var(--red); font-size:11px;">{stageError[row.client]}</span>
                        {/if}
                        <label class="text-xs text-dim" for="followup-{row.client}">Follow-up</label>
                        {#if editFollowup === row.client}
                          <input id="followup-{row.client}" type="date"
                            bind:value={editFollowupDate}
                            onkeydown={(e) => { if (e.key === 'Enter') saveFollowupDate(row.client); if (e.key === 'Escape') editFollowup = null; }}
                          />
                          <div class="row" style="gap:6px;">
                            <button style="flex:1; font-size:12px;" onclick={() => saveFollowupDate(row.client)}>Save</button>
                            <button style="flex:1; font-size:12px; opacity:0.7;" onclick={() => editFollowup = null}>Cancel</button>
                          </div>
                        {:else}
                          <button class="row-detail-edit-btn" onclick={() => { editFollowup = row.client; editFollowupDate = row.follow_up !== '—' ? row.follow_up : ''; }}>
                            {row.follow_up === '—' ? 'Set follow-up…' : row.follow_up}
                          </button>
                        {/if}
                        <label class="text-xs text-dim" for="rename-{row.client}">Client name</label>
                        {#if renamingClient === row.client}
                          <input id="rename-{row.client}" type="text" bind:value={renameDraft}
                            onkeydown={(e) => { if (e.key === 'Enter') saveRename(row.client); if (e.key === 'Escape') { renamingClient = null; renameDraft = ''; renameError = ''; } }}
                          />
                          <div class="row" style="gap:6px;">
                            <button style="flex:1; font-size:12px;" disabled={renameSaving} onclick={() => saveRename(row.client)}>{renameSaving ? '…' : 'Save'}</button>
                            <button style="flex:1; font-size:12px; opacity:0.7;" onclick={() => { renamingClient = null; renameDraft = ''; renameError = ''; }}>Cancel</button>
                          </div>
                          {#if renameError}
                            <span style="color:var(--red); font-size:11px;">{renameError}</span>
                          {/if}
                        {:else}
                          <button class="row-detail-edit-btn" onclick={() => { renamingClient = row.client; renameDraft = row.client.replace(/_/g, ' '); renameError = ''; }}>
                            Rename…
                          </button>
                        {/if}
                        <button class="row-detail-open-btn" onclick={() => $currentView = row.client}>
                          Open {$terms.counterparty} →
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              {/if}
            {/each}
            {#if grouped[stageName].length > _limit}
              <tr class="load-more-row">
                <td colspan="7" style="padding:12px; text-align:center;">
                  <button
                    onclick={() => loadMore(stageName)}
                    style="padding:6px 16px; font-size:12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:4px; cursor:pointer;"
                  >
                    Load {Math.min(CHUNK_INCREMENT, grouped[stageName].length - _limit)} more ({grouped[stageName].length - _limit} remaining)
                  </button>
                </td>
              </tr>
            {/if}
        {/if}
      {/if}
    {/each}
    </tbody>
  </table>
  </div>
{/if}

{#if parkedCount > 0 && !stageFilter.includes('Non-Clients')}
  <button class="row-detail-edit-btn" style="margin-top:12px;" onclick={showNonClients}>
    Show {$stageLabel('Non-Clients')} ({parkedCount}) →
  </button>
{/if}

<UpgradeModal show={showUpgrade} onClose={() => showUpgrade = false} />

<style>
  /* ─── View dropdown ─── */
  .dash-view-bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }
  .vdt {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 7px 12px 7px 14px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 7px;
    cursor: pointer;
    transition: all 0.12s;
  }
  .vdt:hover { background: var(--bg-hover); border-color: var(--border-strong); }
  /* Same minimal coral ribbon as the dropdown rows (always "on" here — the
     toggle only shows it when the active view is bookmarked). */
  .vdt-bm { display: inline-flex; align-items: center; color: var(--accent-ink); }
  .vdt-bm svg { width: 12px; height: 12px; }
  .vdt-bm svg path { fill: currentColor; stroke: currentColor; stroke-width: 2; stroke-linejoin: round; }
  .vdt-name { font-size: 14px; font-weight: 500; color: var(--text); }
  .vdt-arrow { color: var(--text-dim); font-size: 10px; margin-left: 2px; }
  .vdm {
    position: absolute;
    top: 44px;
    left: 0;
    width: 280px;
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.18);
    padding: 6px 0;
    z-index: 100;
  }
  .vdm-section-label {
    font-size: 9.5px;
    color: var(--text-dim);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 6px 14px 4px;
  }
  .vdm-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 14px;
    font-size: 13px;
    color: var(--text-muted);
    cursor: pointer;
  }
  .vdm-item:hover { background: var(--bg-hover); color: var(--text); }
  .vdm-item.active { background: var(--bg-hover); color: var(--text); font-weight: 500; }
  /* Minimal bookmark: a small outline ribbon that fills accent when on — quieter
     than the 🔖 emoji and distinct from the pin ★. */
  .vdm-bm {
    display: inline-flex; align-items: center; justify-content: center;
    width: 18px; flex-shrink: 0; padding: 0;
    border: none; background: none; cursor: pointer;
    color: var(--text-dim); transition: color 0.1s;
  }
  .vdm-bm svg { width: 12px; height: 12px; }
  .vdm-bm svg path { fill: none; stroke: currentColor; stroke-width: 2; stroke-linejoin: round; }
  .vdm-bm:hover { color: var(--text-muted); }
  .vdm-bm.on { color: var(--accent-ink); }
  .vdm-bm.on svg path { fill: currentColor; }
  .vdm-name { flex-grow: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .vdm-divider { height: 1px; background: var(--border); margin: 4px 0; }

  /* ─── Saved-view bar extras (dropdown base .vdt/.vdm is above) ─── */
  .vdt-dirty { font-size: 12px; color: var(--accent-ink); font-style: italic; }
  .dash-view-spacer { flex-grow: 1; }
  .dash-save-btn {
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--accent) 42%, transparent);
    color: var(--accent-ink);
    padding: 7px 13px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
  }
  .dash-save-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--accent) 22%, transparent); }
  .dash-save-btn:disabled { opacity: 0.4; cursor: default; }
  /* Secondary "Save as new" next to the primary "Update view" — quieter/neutral. */
  .dash-save-btn.ghost {
    background: none;
    border-color: var(--border);
    color: var(--text-muted);
  }
  .dash-save-btn.ghost:hover { border-color: var(--border-strong); color: var(--text); background: var(--bg-hover); }
  .vdm-count { margin-left: auto; font-size: 11px; font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace; color: var(--text-dim); }
  /* Rename (pencil) + delete (✕) — revealed on row hover so the menu stays quiet
     at rest. Rename edits the name in place (no modal); delete is the red one. */
  .vdm-act {
    flex-shrink: 0;
    display: inline-flex; align-items: center;
    border: none; background: none;
    padding: 0 2px; font-size: 12px; line-height: 1;
    color: var(--text-dim); cursor: pointer;
    opacity: 0; transition: opacity 0.1s, color 0.1s;
  }
  .vdm-rename svg { width: 13px; height: 13px; }
  .vdm-rename svg path { fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .vdm-item:hover .vdm-act, .vdm-act:focus-visible { opacity: 0.6; }
  .vdm-act:hover { color: var(--text); opacity: 1; }
  .vdm-del:hover, .vdm-del:focus-visible { color: var(--red); opacity: 1; }
  /* Inline rename input — replaces the name span in place. */
  .vdm-edit {
    flex: 1; min-width: 0; margin: -2px 0;
    background: var(--bg-surface); border: 1px solid var(--border-strong);
    border-radius: 5px; padding: 3px 7px;
    color: var(--text); font: inherit; font-size: 13px;
  }
  .vdm-edit:focus { outline: none; border-color: var(--accent); }

  /* ─── Filter toolbar: search + Stage/Status/Owner chip-dropdowns + Clear ─── */
  /* The toolbar (`.page-toolbar`, `.page-toolbar-search`) and the filter chips (`.fchip*`)
     are in app.css — Contacts renders the same bar. Nothing page-specific left here. */
  /* The filter chips (`.fchip*`) live in app.css — Contacts uses them too. Only add a
     rule here for something genuinely Dashboard-specific. */

  /* ─── Save-current-view modal ─── */
  .dash-modal-bg {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dash-modal {
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: 12px;
    padding: 22px;
    width: 340px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }
  .dash-modal h3 { font-size: 15px; margin-bottom: 14px; color: var(--text); }
  .dash-modal-input {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 9px 11px;
    color: var(--text);
    font-size: 13px;
    margin-bottom: 14px;
    font-family: inherit;
  }
  .dash-modal-input:focus { outline: none; border-color: var(--border-strong); }
  .dash-modal-row { display: flex; gap: 8px; justify-content: flex-end; }
  .dash-mbtn {
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-muted);
    font-family: inherit;
  }
  .dash-mbtn:hover { border-color: var(--border-strong); color: var(--text); }
  .dash-mbtn.primary { background: var(--accent); border-color: var(--accent); color: #fff; }
  .dash-mbtn.primary:hover { background: color-mix(in srgb, var(--accent) 88%, black); }

  /* ── Responsive: progressively hide low-priority columns on narrow viewports ── */
  /* < 900px: drop Contacts + Activity (rightmost two, lowest value at-a-glance) */
  @media (max-width: 900px) {
    .data-table .col-contacts,
    .data-table .col-activity { display: none; }
  }
  /* < 720px: drop Last contact. Status + Follow-up are the load-bearing
     columns when scanning what needs attention. (Stage column removed.) */
  @media (max-width: 720px) {
    .data-table .col-date { display: none; }
    .data-table th, .data-table td { padding: 8px 10px; font-size: 12px; }
  }
  /* < 480px: tighten further; status badge wraps cleanly inside the Client cell */
  @media (max-width: 480px) {
    .data-table .col-status,
    .data-table .col-io { display: none; }  /* keep Score · Client · Follow-up */
    .data-table th, .data-table td { padding: 6px 8px; font-size: 12px; }
  }
  /* This toolbar replaced a stage-chip + "+ Add filter" chip row, per
     designs/dashboard/dashboard-stage-grouped-full.html. Contacts followed it onto the
     same `.fchip*` dropdowns; the `.fc-*` and `.stage-chip` rules both pages left behind
     in app.css were deleted once nothing rendered them. */

  /* ─── Data table ─── */
  /* The table, group-header and stage-dot rules all come from app.css — this page used
     to carry a byte-identical private copy of 19 of them (two hardcoding `--font-mono`'s
     literal value), which is how a shared vocabulary drifts. Only this page's own
     overrides live here now: the responsive column drops and `td.cell-pin` / `td.cell-score`. */

  /* Score cell */


  /* Per-row Stage pill removed (stage shows in the group header only) —
     stage-pill-* styles deleted with the column. DESIGN-SYSTEM.md §6. */

  /* Status badge */
  .status-badge-new {
    display: inline-flex;
    padding: 3px 9px;
    border-radius: 12px;
    font-size: 10.5px;
    font-weight: 500;
  }

  /* Follow-up date is always neutral — the Status badge carries the color
     (DESIGN-SYSTEM.md §6). No date-overdue / date-warm tinting. */

  /* Contacts pill */
  .contacts-pill {
    display: inline-flex;
    padding: 2px 7px;
    border-radius: 3px;
    background: var(--bg);
    color: var(--text-dim);
    font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
    font-size: 11px;
  }

  /* Pin star button */
  /* Pin (star) is a narrow icon column paired with Score — hug it toward the
     score so the two read as one unit, instead of floating a lone star far left.
     Specificity must beat `.data-table td { padding: 10px 14px }`, hence the
     `td.cell-*` qualifier. */
  .data-table td.cell-pin { text-align: center; padding-right: 4px; }
  .data-table td.cell-score { padding-left: 10px; }

  /* Expanded detail row */
  .detail-row td {
    padding: 0;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }
  .row-detail {
    display: flex;
    gap: 24px;
    padding: 16px 20px;
  }
  .row-detail-main { flex: 1; min-width: 0; }
  .row-detail-side {
    width: 200px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .row-detail-side label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-dim);
    font-weight: 600;
    margin-bottom: -4px;
  }
  .row-detail-sub {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px dashed var(--border);
  }
  .row-detail-edit-btn,
  .row-detail-open-btn {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 5px;
    padding: 6px 10px;
    font-size: 12px;
    color: var(--text);
    cursor: pointer;
    text-align: left;
  }
  .row-detail-edit-btn:hover { border-color: var(--border-strong); }
  .row-detail-open-btn {
    margin-top: 4px;
    background: var(--accent);
    color: #fff;
    border-color: var(--accent);
    text-align: center;
    font-weight: 500;
  }
  .row-detail-open-btn:hover { background: color-mix(in srgb, var(--accent) 88%, black); }

  .score-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
    letter-spacing: 0.3px;
  }
  .signals-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    padding: 1px 6px;
    border-radius: 8px;
    background: rgba(96, 165, 250, 0.12);
    color: var(--blue, #60a5fa);
    cursor: pointer;
    flex-shrink: 0;
  }
  .signals-badge:hover {
    background: rgba(96, 165, 250, 0.22);
  }
  .signals-panel {
    padding: 6px 12px 8px 40px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid var(--border, #333);
    background: rgba(96, 165, 250, 0.03);
  }
  .signal-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }
  .signal-link {
    color: var(--blue, #60a5fa);
    text-decoration: none;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .signal-link:hover {
    text-decoration: underline;
  }
  .score-panel {
    padding: 10px 16px 10px 40px;
    border-bottom: 1px solid var(--border, #333);
    background: rgba(96, 165, 250, 0.03);
  }
  .score-panel-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .score-panel-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
  }
  .score-panel-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text, #e5e5e5);
  }
  .score-panel-tag {
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .score-panel-tag.outbound {
    background: rgba(156, 163, 175, 0.15);
    color: #9ca3af;
  }
  .score-panel-tag.early {
    background: rgba(167, 139, 250, 0.12);
    color: #a78bfa;
  }
  .score-panel-tag.moderate {
    background: rgba(45, 212, 191, 0.12);
    color: #2dd4bf;
  }
  .score-panel-tag.strong {
    background: rgba(34, 197, 94, 0.12);
    color: #22c55e;
  }
  .score-panel-reasoning {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-dim, #999);
  }

  /* Trial banner */
  .trial-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    margin: 8px 0;
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: var(--radius);
    font-size: 13px;
    color: #fbbf24;
  }
  .trial-upgrade {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: var(--radius);
    border: 1px solid #fbbf24;
    background: transparent;
    color: #fbbf24;
    cursor: pointer;
  }
  .trial-upgrade:hover { background: rgba(251, 191, 36, 0.15); }
  .eval-credit-badge {
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 8px;
    background: rgba(59, 130, 246, 0.12);
    color: #3b82f6;
  }
  .trial-dismiss {
    margin-left: auto;
    background: none;
    border: none;
    color: #fbbf24;
    cursor: pointer;
    font-size: 16px;
    padding: 0 4px;
  }

  /* George messages */
  .george-panel {
    margin: 12px 0;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--bg-surface);
    overflow: hidden;
  }
  .george-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    background: rgba(59, 130, 246, 0.04);
    border-bottom: 1px solid var(--border);
  }
  .george-panel-header:hover { background: rgba(59, 130, 246, 0.08); }
  .george-avatar { width: 20px; height: 20px; object-fit: contain; }
  .george-name { font-weight: 600; font-size: 14px; color: var(--text); }
  .george-unread-count {
    font-size: 11px;
    background: #3b82f6;
    color: #fff;
    padding: 1px 8px;
    border-radius: 10px;
  }
  .george-mark-read {
    font-size: 11px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 2px 6px;
  }
  .george-mark-read:hover { color: var(--text); text-decoration: underline; }
  .george-chevron { color: var(--text-muted); font-size: 12px; }
  .george-messages { padding: 4px 0; }
  .george-msg {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border);
    transition: background 0.1s;
  }
  .george-msg:last-child { border-bottom: none; }
  .george-msg:hover { background: var(--bg-hover); }
  .george-unread {
    background: rgba(59, 130, 246, 0.08);
    border-right: 2px solid rgba(59, 130, 246, 0.3);
  }
  .george-msg-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .george-msg-client {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent-ink);
    cursor: pointer;
  }
  .george-msg-client:hover { text-decoration: underline; }
  .george-msg-time { font-size: 11px; color: var(--text-dim); }
  .george-msg-content {
    font-size: 13px;
    line-height: 1.6;
    color: var(--text);
  }
  .george-msg-content :global(ol),
  .george-msg-content :global(ul) {
    margin: 8px 0;
    padding-left: 24px;
  }
  .george-msg-content :global(li) {
    margin-bottom: 6px;
  }
  .george-msg-content :global(p) {
    margin: 8px 0;
  }
  .george-msg-content :global(h1),
  .george-msg-content :global(h2),
  .george-msg-content :global(h3) {
    margin: 12px 0 6px;
    font-size: 14px;
    font-weight: 600;
  }
  .george-msg-actions { margin-top: 6px; }
  .george-reply-btn {
    font-size: 11px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 2px 0;
  }
  .george-reply-btn:hover { color: var(--accent-ink); }
  .george-reply-box {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .george-show-more {
    display: block;
    width: 100%;
    padding: 8px;
    background: none;
    border: none;
    border-top: 1px solid var(--border);
    color: var(--text-muted);
    font-size: 12px;
    cursor: pointer;
    text-align: center;
  }
  .george-show-more:hover { color: var(--accent-ink); background: var(--bg-hover); }
  .dash-search { max-width: 280px; width: 100%; }
  .client-detail-row { display: flex; gap: 16px; align-items: flex-start; }
  .followup-edit { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .rename-edit { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .rename-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--text-muted);
    font-size: 12px;
    padding: 2px 4px;
    margin-left: 2px;
    opacity: 0;
    transition: opacity 0.1s;
  }
  tr:hover .rename-btn { opacity: 1; }
  .rename-btn:hover { color: var(--accent-ink); }

  @media (max-width: 768px) {
    .dash-search { max-width: 100%; }
    .stage-pill { font-size: 11px; padding: 3px 8px; }
    .score-badge { min-width: 24px; height: 18px; font-size: 10px; }
    .client-detail-row { flex-direction: column; gap: 8px; }
    .followup-edit { display: flex; width: 100%; margin-top: 4px; }
    :global(.expander-header) { flex-wrap: wrap; }
    .george-msg { padding: 10px; }
    .george-msg-content { font-size: 13px; }
    .george-msg-actions { flex-wrap: wrap; }
  }

  @media (max-width: 480px) {
    .stage-pill { font-size: 10px; padding: 2px 6px; }
  }

  /* ── Expanded pipeline row — mobile layout ──
     The side panel is fixed 200px on desktop, which crushes the summary text
     into a narrow column on phones. Stack vertically, lay the controls out as
     a 2-up grid, and hide the now-redundant text labels — the controls speak
     for themselves in context. Edit-mode inputs (date / rename) span both
     columns so they keep room to type. */
  @media (max-width: 720px) {
    .row-detail { flex-direction: column; gap: 14px; padding: 14px; }
    .row-detail-side {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 10px;
      row-gap: 8px;
    }
    .row-detail-side > label { display: none; }
    .row-detail-side > input,
    .row-detail-side > .row { grid-column: 1 / -1; }
    .row-detail-edit-btn,
    .row-detail-open-btn { text-align: center; }
  }
  /* Narrowest phones — drop the side panel to single column so each control
     gets full width and labels-in-buttons stay readable. */
  @media (max-width: 360px) {
    .row-detail-side { grid-template-columns: 1fr; }
  }
</style>
